import { z } from 'zod';
import { NotificationContentCloneSchema, NotificationContentCreateSchema, NotificationContentListQuerySchema, NotificationContentListResponseSchema, NotificationContentParamsSchema, NotificationContentSchema, NotificationContentUpdateSchema, } from './content.js';
import { NotificationIntentFindAllQuerySchema, NotificationIntentSchema } from './intent.js';
import { NotificationRequestListQuerySchema, NotificationRequestListResponseSchema, } from './request.js';
import { NotificationScheduleListQuerySchema, NotificationScheduleListResponseSchema, NotificationScheduleWithTimingsSchema, } from './schedule.js';
import { NotificationSchemeFullSchema, NotificationSchemeListQuerySchema, NotificationSchemeListResponseSchema, NotificationSchemeParamsSchema, } from './scheme.js';
const TAGS = ['Notifications'];
export const NOTIFICATION_CONTENTS_BASE_ROUTE = '/notification-contents';
export const NOTIFICATION_INTENTS_BASE_ROUTE = '/notification-intents';
export const NOTIFICATION_SCHEMES_BASE_ROUTE = '/notification-schemes';
export const NOTIFICATION_SCHEDULES_BASE_ROUTE = '/notification-schedules';
export const NOTIFICATION_REQUESTS_BASE_ROUTE = '/notification-requests';
export const NotificationContentListRoute = {
    method: 'get',
    fullPath: NOTIFICATION_CONTENTS_BASE_ROUTE,
    summary: 'List notification contents',
    description: 'Lists the message templates an organization can send, filterable by channel, name, and ' +
        'owning organization. Each item carries the content name, its channel, one localization per ' +
        'language, and the reply intents that branch a conversation on from it. Results are scoped ' +
        'to the organizations the caller can access, and to manually authored contents unless ' +
        'another source is asked for. Results are one page of contents ordered by name. Use it to ' +
        'find the template behind a message, ' +
        'or to see what an organization can send on a channel.',
    tags: TAGS,
    /*
     * Paging without sorting: the handler validates `sort` against no sortable columns, so every
     * value it could carry is rejected. Publishing it would advertise a parameter the server 400s on.
     */
    query: NotificationContentListQuerySchema,
    response: NotificationContentListResponseSchema,
};
export const NotificationContentGetRoute = {
    method: 'get',
    fullPath: `${NOTIFICATION_CONTENTS_BASE_ROUTE}/:contentId`,
    summary: 'Get a notification content by ID',
    description: 'Returns one message template: its name, the channel it is written for, its localizations, ' +
        'and the reply intents defined on it. Use it after finding a content id, to read the exact ' +
        'text a recipient is sent in a given language.',
    tags: TAGS,
    params: NotificationContentParamsSchema,
    response: NotificationContentSchema,
};
export const NotificationContentCreateRoute = {
    method: 'post',
    fullPath: NOTIFICATION_CONTENTS_BASE_ROUTE,
    summary: 'Create a notification content',
    tags: TAGS,
    body: NotificationContentCreateSchema,
    response: NotificationContentSchema,
};
export const NotificationContentCloneRoute = {
    method: 'post',
    fullPath: `${NOTIFICATION_CONTENTS_BASE_ROUTE}/:contentId/clone`,
    summary: 'Copy a notification content under a new name',
    tags: TAGS,
    params: NotificationContentParamsSchema,
    body: NotificationContentCloneSchema,
    response: z.object({ id: z.uuid() }),
};
export const NotificationContentUpdateRoute = {
    method: 'patch',
    fullPath: `${NOTIFICATION_CONTENTS_BASE_ROUTE}/:contentId`,
    summary: 'Update a notification content',
    tags: TAGS,
    params: NotificationContentParamsSchema,
    body: NotificationContentUpdateSchema,
    response: NotificationContentSchema,
};
export const NotificationContentRemoveRoute = {
    method: 'delete',
    fullPath: `${NOTIFICATION_CONTENTS_BASE_ROUTE}/:contentId`,
    summary: 'Delete a notification content',
    description: 'Soft-deletes the content if anything still references it, hard-deletes it otherwise.',
    tags: TAGS,
    params: NotificationContentParamsSchema,
    response: z.object({ success: z.boolean() }),
};
export const NotificationScheduleListRoute = {
    method: 'get',
    fullPath: `${NOTIFICATION_SCHEDULES_BASE_ROUTE}/list`,
    summary: 'List notification schedules',
    description: 'Lists the named sequences of send times an organization uses, filterable by name and owning ' +
        'organization. Each item carries the schedule name, its owning organization, how many timings ' +
        'it has, and what those timings are anchored to. Results are scoped to the organizations the ' +
        'caller can access, and leave out deleted schedules unless asked; an agent sees only ' +
        'schedules owned by an organization in its grant, never shared ones. Results are one page of ' +
        'schedules ordered by name. Use it to ' +
        'find a schedule by name before reading its timings.',
    tags: TAGS,
    query: NotificationScheduleListQuerySchema,
    response: NotificationScheduleListResponseSchema,
};
export const NotificationScheduleGetRoute = {
    method: 'get',
    fullPath: `${NOTIFICATION_SCHEDULES_BASE_ROUTE}/:scheduleId`,
    summary: 'Get a notification schedule by ID',
    description: 'Returns one schedule: its name, the organization that owns it, and every timing in it with ' +
        'the rules that place that timing relative to an appointment or to when a form set was ' +
        'created, including the time of day a send is allowed to land in. Use it to answer when ' +
        'outreach goes out, and how far ahead of a visit each message is sent. The message text ' +
        'itself belongs to the notification content a scheme pairs with each timing.',
    tags: TAGS,
    params: z.object({ scheduleId: z.uuid() }),
    response: NotificationScheduleWithTimingsSchema,
};
export const NotificationSchemeListRoute = {
    method: 'get',
    fullPath: `${NOTIFICATION_SCHEMES_BASE_ROUTE}/list`,
    summary: 'List notification schemes',
    description: 'Lists the outreach programs an organization runs, filterable by name and owning ' +
        'organization. Each item carries the scheme name, its owning organization, its priority, the ' +
        'channels it sends on, and the previsit form templates that select it. Results are scoped to ' +
        'the organizations the caller can access, and leave out deleted schemes unless asked. ' +
        'Results are one page of schemes ordered by name. Use it to find a scheme by name before ' +
        'reading what it sends and when.',
    tags: TAGS,
    query: NotificationSchemeListQuerySchema,
    response: NotificationSchemeListResponseSchema,
};
export const NotificationSchemeGetRoute = {
    method: 'get',
    fullPath: `${NOTIFICATION_SCHEMES_BASE_ROUTE}/:schemeId`,
    summary: 'Get a notification scheme by ID',
    description: 'Returns one scheme: its name, the organization that owns it, its priority, and every ' +
        'pairing it makes of a schedule timing with the content sent at that timing, each with the ' +
        'timing rules and the message text itself. Use it to answer what an outreach program sends ' +
        'and when, in one read. Where two schemes could apply to the same patient, the higher ' +
        'priority wins.',
    tags: TAGS,
    params: NotificationSchemeParamsSchema,
    response: NotificationSchemeFullSchema,
};
export const NotificationIntentListRoute = {
    method: 'get',
    fullPath: NOTIFICATION_INTENTS_BASE_ROUTE,
    summary: 'List the reply intents defined across notification contents',
    description: 'Lists the reply intents that branch notification conversations, optionally narrowed to one ' +
        'notification content. Each item gives the intent label, the replies that select it, and the ' +
        "content sent back when they do. Use it to explain how a recipient's answer is interpreted " +
        'and what follows it.',
    tags: TAGS,
    query: NotificationIntentFindAllQuerySchema,
    response: z.array(NotificationIntentSchema),
};
export const NotificationRequestListRoute = {
    method: 'get',
    fullPath: `${NOTIFICATION_REQUESTS_BASE_ROUTE}/list`,
    summary: 'List notification send history',
    description: 'Lists notification requests, what Notable tried to send by SMS, email, or voice, newest ' +
        'first, each with its provider send attempts and their delivery outcome. Agents only. Pass ' +
        'ehrInformationId, encounterId, or both to intersect them, with a required createdAfter and ' +
        'createdBefore window that must end after it starts; a request with neither is refused. ' +
        'Covers practices of organizations the caller is a direct member of and the run was ' +
        'granted. Never returns message bodies, phone numbers, or email addresses.',
    tags: TAGS,
    capabilityTags: ['ResponseCouldIncludePhi'],
    query: NotificationRequestListQuerySchema,
    response: NotificationRequestListResponseSchema,
};
//# sourceMappingURL=routes.js.map